import React, { useEffect, useState, useRef } from "react";
import { ym } from "../lib/dates";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Plus, PencilSimple, Trash, Printer, Funnel, Stack, FileArrowUp, Download, Warning, ClockCounterClockwise, ArrowsClockwise } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { formatApiError, formatIDR } from "../lib/api";
import { useAuth } from "../lib/auth";
import { generatePDF } from "../lib/pdf";
import Pagination from "../components/Pagination";

export default function Tagihan() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [siswaList, setSiswaList] = useState([]);
  const [lembagas, setLembagas] = useState([]);
  const [NOMEN, setNOMEN] = useState([]);
  const [filterSiswa, setFilterSiswa] = useState("all");
  const [searchSiswa, setSearchSiswa] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterLembaga, setFilterLembaga] = useState("all");
  const [filterPeriod, setFilterPeriod] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importLembaga, setImportLembaga] = useState("");
  const [importFile2, setImportFile2] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [batchesOpen, setBatchesOpen] = useState(false);
  const [batches, setBatches] = useState([]);
  const [batchesBusy, setBatchesBusy] = useState(false);
  const [ippOpen, setIppOpen] = useState(false);
  const [ippForm, setIppForm] = useState({});
  const [ippPreview, setIppPreview] = useState(null);
  const [ippBusy, setIppBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [bulkForm, setBulkForm] = useState({});
  const fileInput = useRef();
  const canManage = ["superadmin","admin","finance"].includes(user?.role);

  const reqRef = useRef(0);
  const load = async () => {
    const rid = ++reqRef.current;
    const q = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (filterLembaga !== "all") q.set("lembaga_id", filterLembaga);
    if (filterStatus !== "all") q.set("status", filterStatus);
    if (filterPeriod) q.set("period", filterPeriod);
    if (searchSiswa.trim()) q.set("q", searchSiswa.trim());
    const t = await api.get(`/tagihan-paged?${q}`);
    if (rid !== reqRef.current) return; // abaikan respons lama (filter sudah berubah)
    setItems(t.data.items || []);
    setTotal(t.data.total || 0);
    setTotalPages(t.data.total_pages || 1);
  };
  useEffect(() => {
    Promise.all([api.get("/siswa"), api.get("/lembaga"), api.get("/nomenklatur")])
      .then(([s, l, n]) => { setSiswaList(s.data); setLembagas(l.data); setNOMEN(n.data); })
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [page, filterLembaga, filterStatus, filterPeriod]);
  useEffect(() => { setPage(1); }, [filterLembaga, filterStatus, filterPeriod]);
  // debounce pencarian ke server supaya data di semua halaman tetap ditemukan
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 400); return () => clearTimeout(t); }, [searchSiswa]);

  const siswaMap = Object.fromEntries(siswaList.map((s) => [s.id, s]));

  const openNew = () => {
    setEditing(null);
    setForm({ siswa_id: siswaList[0]?.id || "", nomenklatur_code: "IPP", amount: 0, period: ym(), due_date: "", description: "" });
    setOpen(true);
  };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };

  const openBulk = () => {
    setBulkForm({ lembaga_id: "", class_name: "", nomenklatur_code: "IPP", amount: 0, period: ym(), due_date: "", description: "" });
    setBulkOpen(true);
  };

  const saveBulk = async () => {
    try {
      const payload = { ...bulkForm, lembaga_id: bulkForm.lembaga_id || null, class_name: bulkForm.class_name || null, amount: Number(bulkForm.amount || 0) };
      const { data } = await api.post("/tagihan/generate", payload);
      toast.success(`${data.inserted} tagihan berhasil dibuat untuk ${data.siswa_count} siswa`);
      setBulkOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadTemplate = async () => {
    // Fetch data siswa aktif untuk sheet referensi
    let siswaList = [];
    try {
      const { data } = await api.get("/siswa");
      siswaList = data.filter((s) => s.status === "aktif");
    } catch { /* ignore */ }
    // Sheet 1: Template Tagihan
    const headers = ["nis", "nama", "period", ...NOMEN.map((n) => n.code)];
    const example = [];
    if (siswaList.length > 0) {
      const s = siswaList[0];
      const row = [s.nis, s.name, "2026-08", ...NOMEN.map((n) => n.code === "IPP" ? 300000 : 0)];
      example.push(row);
    } else {
      example.push(["202500001", "Ahmad Fauzi", "2026-08", ...NOMEN.map((n) => n.code === "IPP" ? 300000 : 0)]);
    }
    // Sheet 2: Referensi siswa aktif — user copy NIS ke sheet Template
    const refRows = [
      ["DAFTAR SISWA AKTIF — copy NIS ke kolom 'nis' di sheet Template"],
      [],
      ["nis", "nama", "kelas", "lembaga"],
      ...siswaList.map((s) => [s.nis, s.name, s.class_name || "-", s.lembaga_name || s.lembaga_id || "-"]),
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([headers, ...example]), "Template");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(refRows), "Referensi Siswa");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ["PANDUAN"],
      [],
      ["1. Buka sheet 'Referensi Siswa' → copy NIS siswa target"],
      ["2. Pindah ke sheet 'Template' → paste NIS, isi nama & period"],
      ["3. Isi kolom nomenklatur (IPP/SPP/dll) dengan nominal. Kosongkan atau 0 kalau tidak ditagih."],
      ["4. period format: YYYY-MM contoh: 2026-08 untuk Agustus 2026, atau 2026/2027 untuk tahunan"],
      ["5. Save & upload lewat tombol 'Import Excel'"],
      [],
      ["CATATAN IPP:"],
      ["Untuk IPP bulanan Juli-Juni, PAKAI TOMBOL 'IPP Tahunan (12 bln)' — bukan import Excel"],
      ["Kalau sudah pakai IPP Tahunan, JANGAN isi kolom IPP di template ini (akan dobel)"],
      ["Import Excel cocok untuk: SPP semester, uang gedung, buku, seragam, event insidental"],
    ]), "Panduan");
    XLSX.writeFile(wb, "template-tagihan.xlsx");
  };

  const importFile = async () => {
    if (!importLembaga) { toast.error("Pilih lembaga tujuan dulu"); return; }
    if (!importFile2) { toast.error("Pilih file Excel dulu"); return; }
    setImportBusy(true);
    try {
      const buf = await importFile2.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet);
      if (!data.length) { toast.error("Excel kosong"); setImportBusy(false); return; }
      const nomenCodes = NOMEN.map((n) => n.code);
      const rows = data.map((d) => {
        const amounts = {};
        nomenCodes.forEach((code) => {
          if (d[code] && Number(d[code]) > 0) amounts[code] = Number(d[code]);
        });
        return {
          nis: String(d.nis || d.NIS || ""),
          period: String(d.period || d.PERIOD || ym()),
          amounts,
        };
      }).filter((r) => r.nis && Object.keys(r.amounts).length > 0);
      if (!rows.length) { toast.error("Tidak ada data valid untuk diimport"); setImportBusy(false); return; }
      const { data: res } = await api.post(`/tagihan/bulk-excel?lembaga_id=${importLembaga}`, rows);
      toast.success(`${res.inserted} tagihan dibuat di ${res.lembaga_name} dari ${res.total_rows} baris (batch ${res.import_batch_id})`);
      if (res.errors?.length) alert(`${res.errors.length} baris gagal:\n` + res.errors.slice(0, 10).map((e) => `NIS ${e.nis}: ${e.error}`).join("\n"));
      setImportOpen(false); setImportFile2(null); setImportLembaga("");
      load();
    } catch (e) { toast.error("Gagal import: " + formatApiError(e.response?.data?.detail || e.message)); }
    finally { setImportBusy(false); }
  };

  const openBatches = async () => {
    setBatchesOpen(true);
    setBatchesBusy(true);
    try {
      const { data } = await api.get("/tagihan/import-batches");
      setBatches(data.batches || []);
    } catch (e) { toast.error("Gagal ambil daftar batch: " + formatApiError(e.response?.data?.detail)); }
    finally { setBatchesBusy(false); }
  };

  const deleteBatch = async (batchId, count) => {
    if (!window.confirm(`Yakin hapus ${count} tagihan dari batch ${batchId}?\nTagihan yang sudah ada pembayaran akan di-skip otomatis.`)) return;
    try {
      const { data } = await api.delete(`/tagihan/import-batch/${batchId}`);
      if (data.skipped_paid) toast.warning(`${data.skipped_paid} tagihan di-skip karena sudah ada pembayaran. Hapus manual.`);
      else toast.success(`${data.deleted} tagihan dihapus`);
      openBatches();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    try {
      const payload = { ...form, amount: Number(form.amount || 0) };
      if (editing) await api.put(`/tagihan/${editing.id}`, payload);
      else await api.post("/tagihan", payload);
      toast.success("Tagihan tersimpan"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!confirm("Hapus tagihan?")) return;
    try { await api.delete(`/tagihan/${id}`); toast.success("Terhapus"); load(); } catch { toast.error("Gagal"); }
  };

  const [reconcileBusy, setReconcileBusy] = useState(false);
  const reconcile = async () => {
    // Preview dulu (tidak mengubah data)
    try {
      const { data: pv } = await api.get("/tagihan/reconcile-preview");
      if (pv.drift_count === 0 && pv.orphan_payments === 0) {
        toast.success(`Sinkron ✓ — ${pv.tagihan_checked} tagihan sudah cocok dengan pembayaran`);
        return;
      }
      const ok = confirm(
        `Ditemukan ketidakcocokan:\n` +
        `• ${pv.drift_count} tagihan dengan paid_amount tidak sesuai pembayaran\n` +
        `• ${pv.orphan_payments} pembayaran belum ter-link ke tagihan\n\n` +
        `Lanjutkan sinkronisasi otomatis?\n(paid_amount tiap tagihan akan dihitung ulang = total pembayaran yang masuk)`
      );
      if (!ok) return;
      setReconcileBusy(true);
      const { data: res } = await api.post("/tagihan/reconcile");
      alert(
        `✅ Sinkronisasi selesai!\n\n` +
        `• ${res.tagihan_checked} tagihan diperiksa\n` +
        `• ${res.tagihan_fixed} tagihan diperbaiki\n` +
        `• ${res.orphans_linked} pembayaran orphan berhasil di-link\n` +
        `• ${res.orphans_remaining} pembayaran masih orphan (tidak ada tagihan cocok)`
      );
      toast.success("Sinkronisasi selesai");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setReconcileBusy(false); }
  };

  const filtered = items; // pagination server-side

  const openIPPYearly = async () => {
    const now = new Date();
    const currentYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
    // Auto-detect periode sumber dari batch import TERAKHIR (biar user tidak salah ketik)
    let defaultSource = `${currentYear}-07`;
    let defaultLembaga = "";
    try {
      const { data } = await api.get("/tagihan/import-batches");
      const latest = data.batches?.[0];
      if (latest && latest.periods?.length) {
        defaultSource = latest.periods[0];  // periode pertama batch terakhir
        defaultLembaga = latest.lembaga_id || "";
      }
    } catch { /* ignore */ }
    setIppForm({
      lembaga_id: defaultLembaga, class_name: "", nomenklatur_code: "IPP",
      source_period: defaultSource,
      academic_year_start: currentYear, due_day: 10,
    });
    setIppPreview(null);
    setIppOpen(true);
  };

  const previewIPPYearly = async () => {
    if (!ippForm.source_period || !/^\d{4}-\d{2}$/.test(ippForm.source_period)) return toast.error("Periode sumber wajib format YYYY-MM (mis. 2025-07)");
    setIppBusy(true);
    try {
      const payload = {
        source_period: ippForm.source_period,
        academic_year_start: Number(ippForm.academic_year_start),
        nomenklatur_code: ippForm.nomenklatur_code || null,
        lembaga_id: ippForm.lembaga_id || null,
        class_name: ippForm.class_name || null,
        due_day: Number(ippForm.due_day) || 10,
        dry_run: true,
      };
      const { data } = await api.post("/tagihan/duplicate-yearly", payload);
      setIppPreview(data);
    } catch (e) {
      setIppPreview(null);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setIppBusy(false); }
  };

  const saveIPPYearly = async () => {
    if (!ippPreview || !ippPreview.will_insert) return toast.error("Klik 'Preview' dulu untuk cek data yang akan digandakan");
    setIppBusy(true);
    try {
      const payload = {
        source_period: ippForm.source_period,
        academic_year_start: Number(ippForm.academic_year_start),
        nomenklatur_code: ippForm.nomenklatur_code || null,
        lembaga_id: ippForm.lembaga_id || null,
        class_name: ippForm.class_name || null,
        due_day: Number(ippForm.due_day) || 10,
        dry_run: false,
      };
      const { data } = await api.post("/tagihan/duplicate-yearly", payload);
      toast.success(`${data.inserted} tagihan digandakan dari ${data.source_count} tagihan sumber × ${data.target_months} bulan (skip ${data.skipped} duplikat)`);
      setIppOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setIppBusy(false); }
  };

  const printPDF = () => {
    const nomenMap = Object.fromEntries(NOMEN.map((n) => [n.code, n.name]));
    generatePDF({
      title: "Laporan Tagihan Siswa",
      subtitle: "EduFoundation",
      headers: ["Siswa", "NIS", "Nomenklatur", "Periode", "Tagihan", "Terbayar", "Sisa", "Status"],
      rows: filtered.map((t) => {
        const s = siswaMap[t.siswa_id] || {};
        const nomLabel = nomenMap[t.nomenklatur_code] ? `${t.nomenklatur_code} - ${nomenMap[t.nomenklatur_code]}` : t.nomenklatur_code;
        return [
          s.name || "-",
          s.nis || "-",
          nomLabel,
          t.period,
          formatIDR(t.amount),
          formatIDR(t.paid_amount),
          formatIDR(t.amount - t.paid_amount),
          t.status,
        ];
      }),
      filename: `tagihan-${Date.now()}.pdf`,
    });
  };

  return (
    <div data-testid="tagihan-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Keuangan</div>
          <h1 className="font-display text-3xl font-black text-gray-900">Tagihan Siswa</h1>
          <p className="text-gray-600 text-sm mt-1">Kelola tagihan setiap siswa per nomenklatur.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={printPDF} data-testid="tagihan-print-button"><Printer size={16} className="mr-1.5" />Cetak PDF</Button>
          {canManage && <>
            <Button variant="outline" onClick={downloadTemplate} data-testid="tagihan-template-button"><Download size={16} className="mr-1.5" />Template Excel</Button>
            <input ref={fileInput} type="file" accept=".xlsx,.xls" hidden onChange={(e) => { if (e.target.files?.[0]) { setImportFile2(e.target.files[0]); setImportOpen(true); e.target.value = ""; }}} data-testid="tagihan-import-file" />
            <Button variant="outline" onClick={()=>fileInput.current?.click()} data-testid="tagihan-import-button"><FileArrowUp size={16} className="mr-1.5" />Import Excel</Button>
            <Button variant="outline" onClick={openBatches} data-testid="tagihan-batches-button" className="border-amber-300 text-amber-700 hover:bg-amber-50"><ClockCounterClockwise size={16} className="mr-1.5" />Riwayat Import</Button>
            <Button variant="outline" onClick={openIPPYearly} data-testid="tagihan-ipp-yearly-button" className="border-[#0B8D95]/40 text-[#0B8D95]"><Stack size={16} className="mr-1.5" />Gandakan 12 Bulan</Button>
            <Button variant="outline" onClick={reconcile} disabled={reconcileBusy} data-testid="tagihan-reconcile-button" className="border-amber-400 text-amber-700 hover:bg-amber-50"><ArrowsClockwise size={16} className="mr-1.5" />{reconcileBusy ? "Sinkronisasi..." : "Sinkronkan Bayar↔Tagihan"}</Button>
            <Button variant="outline" onClick={openBulk} data-testid="tagihan-bulk-button"><Stack size={16} className="mr-1.5" />Generate Massal</Button>
            <Button onClick={openNew} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="tagihan-add-button"><Plus size={16} weight="bold" className="mr-1.5" />Tagihan Baru</Button>
          </>}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row gap-3 flex-wrap">
          <Select value={filterLembaga} onValueChange={setFilterLembaga}>
            <SelectTrigger className="w-full sm:w-52 bg-white" data-testid="tagihan-lembaga-filter"><Funnel size={14} className="mr-2" /><SelectValue placeholder="Filter Lembaga" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              <SelectItem value="all">Semua Lembaga</SelectItem>
              {lembagas.map((l)=><SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={searchSiswa} onChange={(e)=>setSearchSiswa(e.target.value)} placeholder="🔍 Cari nama/NIS siswa..." className="w-full sm:w-64 bg-white" data-testid="tagihan-search-siswa" />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-36 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="unpaid">Belum Bayar</SelectItem>
              <SelectItem value="partial">Sebagian</SelectItem>
              <SelectItem value="paid">Lunas</SelectItem>
            </SelectContent>
          </Select>
          <Input type="month" value={filterPeriod} onChange={(e)=>setFilterPeriod(e.target.value)} className="w-full sm:w-40 bg-white" placeholder="Periode" data-testid="tagihan-period-filter" />
          <div className="text-xs text-gray-500 font-mono self-center ml-auto">
            Hal {page}/{totalPages} · {total} tagihan
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Siswa</th>
                <th className="px-4 py-3 font-semibold">Nomenklatur</th>
                <th className="px-4 py-3 font-semibold">Periode</th>
                <th className="px-4 py-3 font-semibold text-right">Tagihan</th>
                <th className="px-4 py-3 font-semibold text-right">Terbayar</th>
                <th className="px-4 py-3 font-semibold text-right">Sisa</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const s = siswaMap[t.siswa_id] || {};
                const sisa = (t.amount || 0) - (t.paid_amount || 0);
                return (
                  <tr key={t.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-gray-900">{s.name || "-"}</div>
                      <div className="text-xs text-gray-500 font-mono">{s.nis}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-col gap-0.5">
                        <span className="inline-block w-fit px-2 py-0.5 rounded bg-[#CCFBF1] text-[#0B8D95] text-[10px] font-mono font-bold">{t.nomenklatur_code}</span>
                        <span className="text-xs text-gray-700">{(NOMEN.find((n) => n.code === t.nomenklatur_code) || {}).name || "-"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 font-mono">{t.period}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold">{formatIDR(t.amount)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-[#059669]">{formatIDR(t.paid_amount)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-[#DC2626]">{formatIDR(sisa)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                        t.status === "paid" ? "bg-[#CCFBF1] text-[#0B8D95]" :
                        t.status === "partial" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"
                      }`}>{t.status}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {canManage && <>
                        <button onClick={()=>openEdit(t)} className="p-1.5 hover:bg-gray-100 rounded" data-testid={`tagihan-edit-${t.id}`}><PencilSimple size={14} /></button>
                        <button onClick={()=>remove(t.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`tagihan-delete-${t.id}`}><Trash size={14} /></button>
                      </>}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-gray-500">Belum ada tagihan</td></tr>}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onChange={setPage} testIdPrefix="tagihan" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="tagihan-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Tagihan" : "Buat Tagihan"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Lembaga</Label>
                <Select value={form._lembaga_filter || "all"} onValueChange={(v)=>setForm({...form, _lembaga_filter: v, siswa_id: ""})}>
                  <SelectTrigger className="bg-white" data-testid="tagihan-form-lembaga"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    <SelectItem value="all">Semua Lembaga</SelectItem>
                    {lembagas.map((l)=><SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Cari Siswa</Label>
                <Input value={form._siswa_search || ""} onChange={(e)=>setForm({...form, _siswa_search: e.target.value})} placeholder="🔍 nama/NIS..." data-testid="tagihan-form-search" />
              </div>
            </div>
            <div><Label>Pilih Siswa *</Label>
              <Select value={form.siswa_id} onValueChange={(v)=>setForm({...form, siswa_id: v})}>
                <SelectTrigger className="bg-white" data-testid="tagihan-siswa-select"><SelectValue placeholder={(() => {
                  const s = siswaMap[form.siswa_id];
                  return s ? `${s.nis} - ${s.name}` : "Pilih siswa";
                })()} /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {siswaList.filter((s)=> {
                    if (form._lembaga_filter && form._lembaga_filter !== "all" && s.lembaga_id !== form._lembaga_filter) return false;
                    const q = (form._siswa_search || "").trim().toLowerCase();
                    if (q) { const hay = `${s.name||""} ${s.nis||""} ${s.nisn||""}`.toLowerCase(); if (!hay.includes(q)) return false; }
                    return true;
                  }).slice(0, 100).map((s)=><SelectItem key={s.id} value={s.id}>{s.nis} - {s.name} <span className="text-xs text-gray-500">({s.class_name || "-"})</span></SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-gray-500 mt-1">Ketik nama/NIS di kotak "Cari Siswa" untuk mempersempit. Max 100 hasil tampil.</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nomenklatur *</Label>
                <Select value={form.nomenklatur_code} onValueChange={(v)=>setForm({...form, nomenklatur_code: v})}>
                  <SelectTrigger className="bg-white" data-testid="tagihan-nomen-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{NOMEN.map((n)=><SelectItem key={n.code} value={n.code}>{n.code} - {n.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Periode *</Label><Input value={form.period || ""} onChange={(e)=>setForm({...form, period: e.target.value})} placeholder="2025-01 atau 2024/2025" /></div>
              <div><Label>Jumlah (Rp) *</Label><Input type="number" value={form.amount || 0} onChange={(e)=>setForm({...form, amount: e.target.value})} data-testid="tagihan-amount-input" /></div>
              <div><Label>Jatuh Tempo</Label><Input type="date" value={form.due_date || ""} onChange={(e)=>setForm({...form, due_date: e.target.value})} /></div>
            </div>
            <div><Label>Keterangan</Label><Input value={form.description || ""} onChange={(e)=>setForm({...form, description: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="tagihan-save-button">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ippOpen} onOpenChange={setIppOpen}>
        <DialogContent className="bg-white max-w-2xl" data-testid="tagihan-ipp-yearly-dialog">
          <DialogHeader><DialogTitle className="font-display">Gandakan Tagihan 12 Bulan (Jul – Jun)</DialogTitle></DialogHeader>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-900 mb-3">
            <b>Cara pakai:</b> Import dulu tagihan bulan pertama via <b>Template Excel</b>. Lalu buka dialog ini — <b>Periode Sumber otomatis terisi dari batch import terakhir Anda</b>. Cek dulu apakah sudah benar, lalu klik <b>Preview</b>.
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Periode Sumber (dari Excel) *</Label>
                <Input value={ippForm.source_period || ""} onChange={(e)=>{setIppForm({...ippForm, source_period: e.target.value}); setIppPreview(null);}} placeholder="2025-07" data-testid="ipp-source-period" />
                <div className="text-xs text-gray-500 mt-1">Bulan yang <b>sudah</b> ada tagihannya (dari import Excel)</div>
              </div>
              <div><Label>Tahun Ajaran Mulai *</Label>
                <Input type="number" value={ippForm.academic_year_start || ""} onChange={(e)=>{setIppForm({...ippForm, academic_year_start: e.target.value}); setIppPreview(null);}} placeholder="2025" data-testid="ipp-year-input" />
                <div className="text-xs text-gray-500 mt-1">2025 = Jul 2025 s/d Jun 2026</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nomenklatur (opsional)</Label>
                <Select value={ippForm.nomenklatur_code || "ALL"} onValueChange={(v)=>{setIppForm({...ippForm, nomenklatur_code: v === "ALL" ? "" : v}); setIppPreview(null);}}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="ALL">Semua nomenklatur di periode sumber</SelectItem>
                    {NOMEN.map((n)=><SelectItem key={n.code} value={n.code}>{n.code} - {n.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Tanggal Jatuh Tempo</Label>
                <Input type="number" min={1} max={28} value={ippForm.due_day || 10} onChange={(e)=>setIppForm({...ippForm, due_day: e.target.value})} />
                <div className="text-xs text-gray-500 mt-1">Berlaku tiap bulan (1–28)</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Lembaga (opsional)</Label>
                <Select value={ippForm.lembaga_id || "all"} onValueChange={(v)=>{setIppForm({...ippForm, lembaga_id: v === "all" ? "" : v}); setIppPreview(null);}}>
                  <SelectTrigger className="bg-white" data-testid="ipp-lembaga-select"><SelectValue placeholder="Semua lembaga" /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="all">Semua Lembaga</SelectItem>{lembagas.map((l)=><SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Kelas (opsional)</Label>
                <Input value={ippForm.class_name || ""} onChange={(e)=>{setIppForm({...ippForm, class_name: e.target.value}); setIppPreview(null);}} placeholder="Kosong = semua kelas" />
              </div>
            </div>

            {ippPreview && (
              <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3 text-sm space-y-1">
                <div className="font-bold text-emerald-900">Preview:</div>
                <div className="flex justify-between"><span>Tagihan sumber (bulan {ippForm.source_period})</span><b className="font-mono">{ippPreview.source_count}</b></div>
                <div className="flex justify-between"><span>Bulan target</span><b className="font-mono">{ippPreview.target_months} bulan</b></div>
                <div className="flex justify-between"><span>Akan dibuat</span><b className="font-mono text-emerald-700">{ippPreview.will_insert}</b></div>
                <div className="flex justify-between"><span>Skip (sudah ada)</span><b className="font-mono text-amber-700">{ippPreview.will_skip}</b></div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={()=>setIppOpen(false)} disabled={ippBusy}>Batal</Button>
            <Button variant="outline" onClick={previewIPPYearly} disabled={ippBusy} data-testid="ipp-preview-button">{ippBusy && !ippPreview ? "Memuat..." : "Preview"}</Button>
            <Button onClick={saveIPPYearly} disabled={ippBusy || !ippPreview || !ippPreview.will_insert} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="ipp-save-button">{ippBusy && ippPreview ? "Memproses..." : `Gandakan (${ippPreview?.will_insert || 0})`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={(v) => { if (!importBusy) { setImportOpen(v); if (!v) { setImportFile2(null); setImportLembaga(""); } }}}>
        <DialogContent className="bg-white" data-testid="tagihan-import-dialog">
          <DialogHeader><DialogTitle>Import Tagihan dari Excel</DialogTitle></DialogHeader>
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900 flex gap-2 items-start">
            <Warning size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
            <div><b>Pilih Lembaga tujuan WAJIB</b> — supaya NIS di-lookup hanya di siswa lembaga tersebut. Kalau NIS sama muncul di 2 sekolah, tanpa filter ini tagihan bisa nyasar ke sekolah lain.</div>
          </div>
          <div className="space-y-3 mt-3">
            <div>
              <Label>File Excel</Label>
              <div className="text-sm p-2 bg-gray-50 rounded border">📄 {importFile2?.name || "(belum dipilih)"}</div>
            </div>
            <div>
              <Label>Lembaga Tujuan *</Label>
              <Select value={importLembaga} onValueChange={setImportLembaga}>
                <SelectTrigger className="bg-white" data-testid="import-lembaga-select"><SelectValue placeholder="-- Pilih Lembaga --" /></SelectTrigger>
                <SelectContent className="bg-white">{lembagas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportOpen(false); setImportFile2(null); setImportLembaga(""); }} disabled={importBusy}>Batal</Button>
            <Button onClick={importFile} disabled={importBusy || !importLembaga || !importFile2} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="import-confirm-button">{importBusy ? "Mengimport..." : "Import Sekarang"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={batchesOpen} onOpenChange={setBatchesOpen}>
        <DialogContent className="bg-white max-w-4xl" data-testid="tagihan-batches-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ClockCounterClockwise size={20} className="text-amber-700" />Riwayat Import Excel</DialogTitle></DialogHeader>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-900 mb-3">
            Setiap kali Anda klik Import Excel, sistem catat batch-nya. Kalau ada import yang salah/dobel, klik tombol 🗑 di sebelah kanan untuk hapus semua tagihan dari batch tsb. <b>Tagihan yang sudah dibayar akan otomatis di-skip.</b>
          </div>
          {batchesBusy ? <div className="p-6 text-center text-muted-foreground">Memuat...</div> : (
            <div className="max-h-96 overflow-y-auto border rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0"><tr className="text-left">
                  <th className="px-3 py-2 font-bold">Waktu Import</th>
                  <th className="px-3 py-2 font-bold">Lembaga</th>
                  <th className="px-3 py-2 font-bold">Periode</th>
                  <th className="px-3 py-2 font-bold text-right">Jml Tagihan</th>
                  <th className="px-3 py-2 font-bold text-right">Total Rp</th>
                  <th className="px-3 py-2 font-bold text-center">Aksi</th>
                </tr></thead>
                <tbody>
                  {batches.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Belum ada batch import</td></tr>}
                  {batches.map((b) => (
                    <tr key={b.batch_id} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono text-xs">{(b.first_created || "").slice(0, 19).replace("T", " ")}</td>
                      <td className="px-3 py-2 font-semibold">{b.lembaga_name}</td>
                      <td className="px-3 py-2 text-xs">{(b.periods || []).join(", ")}</td>
                      <td className="px-3 py-2 text-right font-mono">{b.count}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatIDR(b.sum_amount || 0)}</td>
                      <td className="px-3 py-2 text-center">
                        <Button size="sm" variant="outline" onClick={() => deleteBatch(b.batch_id, b.count)} data-testid={`batch-delete-${b.batch_id}`} className="text-rose-600 border-rose-300 hover:bg-rose-50"><Trash size={14} /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setBatchesOpen(false)}>Tutup</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="bg-white" data-testid="tagihan-bulk-dialog">
          <DialogHeader><DialogTitle className="font-display">Generate Tagihan Massal</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-500 mb-2">Buat tagihan sekaligus untuk semua siswa aktif dalam lembaga/kelas tertentu.</div>
          <div className="space-y-3">
            <div><Label>Lembaga *</Label>
              <Select value={bulkForm.lembaga_id} onValueChange={(v)=>setBulkForm({...bulkForm, lembaga_id: v})}>
                <SelectTrigger className="bg-white" data-testid="tagihan-bulk-lembaga-select"><SelectValue placeholder="Pilih lembaga" /></SelectTrigger>
                <SelectContent className="bg-white">{lembagas.map((l)=><SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Kelas (opsional — kosongkan untuk semua kelas)</Label><Input value={bulkForm.class_name || ""} onChange={(e)=>setBulkForm({...bulkForm, class_name: e.target.value})} placeholder="Contoh: Kelas 1, Kelas 7, XII IPA 1" data-testid="tagihan-bulk-class-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nomenklatur *</Label>
                <Select value={bulkForm.nomenklatur_code} onValueChange={(v)=>setBulkForm({...bulkForm, nomenklatur_code: v})}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{NOMEN.map((n)=><SelectItem key={n.code} value={n.code}>{n.code} - {n.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Periode *</Label><Input value={bulkForm.period || ""} onChange={(e)=>setBulkForm({...bulkForm, period: e.target.value})} placeholder="2025-01" /></div>
              <div><Label>Jumlah / Siswa *</Label><Input type="number" value={bulkForm.amount || 0} onChange={(e)=>setBulkForm({...bulkForm, amount: e.target.value})} data-testid="tagihan-bulk-amount-input" /></div>
              <div><Label>Jatuh Tempo</Label><Input type="date" value={bulkForm.due_date || ""} onChange={(e)=>setBulkForm({...bulkForm, due_date: e.target.value})} /></div>
            </div>
            <div><Label>Keterangan</Label><Input value={bulkForm.description || ""} onChange={(e)=>setBulkForm({...bulkForm, description: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setBulkOpen(false)}>Batal</Button>
            <Button onClick={saveBulk} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="tagihan-bulk-save-button">Generate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
